import React, { useState, useEffect } from 'react';
import GenerateDocumentModal from '../components/record/GenerateDocumentModal';
import { useParams, useNavigate } from 'react-router-dom';
import { recordsApi } from '../api/records';
import { useAuthStore } from '../store/authStore';
import { adminApi } from '../api/admin';
import api from '../api/client';
import ScreenRenderer, { evaluateVisibility } from '../components/screens/ScreenRenderer';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Clock, Trash2, GitBranch, Mail, Copy, MessageSquare, Sparkles } from 'lucide-react';
import { FieldInput } from './RecordCreate';
import { formatTimestamp } from '../utils/timezone';
import useUnsavedChanges from '../hooks/useUnsavedChanges';
import UnsavedChangesModal from '../components/UnsavedChangesModal';

import ConvertToCaseModal from '../components/intake/ConvertToCaseModal';
import RecordSummary from '../components/record/RecordSummary';
import NotesWidget from '../components/record/NotesWidget';
import ActivityTimeline from '../components/record/ActivityTimeline';
import CommunicationTimeline from '../components/record/CommunicationTimeline';
import DiaryWidget from '../components/record/DiaryWidget';
import client from '../api/client';
import TeamWidget from '../components/record/TeamWidget';
import MiniCalendar from '../components/record/MiniCalendar';
import VitalsWidget from '../components/record/VitalsWidget';
import TaskPreviewWidget from '../components/record/TaskPreviewWidget';
import DocumentPreviewWidget from '../components/record/DocumentPreviewWidget';
import TagsWidget from '../components/record/TagsWidget';
import PortalMessagesWidget from '../components/record/PortalMessagesWidget';
import StageProgressPath from '../components/record/StageProgressPath';
import PartiesPanel from '../components/record/PartiesPanel';
import { ChevronLeft, ChevronRight, X, Link2, Plus, Search } from 'lucide-react';
import { DEFAULT_CONFIG } from '../components/admin/WidgetConfigurator';
import useConcurrentEditing from '../hooks/useConcurrentEditing';
import PresenceBar from '../components/record/PresenceBar';
import ConflictResolutionModal from '../components/record/ConflictResolutionModal';
import RecordChatPanel from '../components/chat/RecordChatPanel';
import AIChatPanel from '../components/chat/AIChatPanel';
import { useChatContext } from '../contexts/ChatContext';
import * as aiChatApi from '../api/aiChat';

export default function RecordDetail() {
  const { typeSlug, recordId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [showDocGenModal, setShowDocGenModal] = useState(false);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [fields, setFields] = useState([]);
  const [screenLayout, setScreenLayout] = useState(null);
  const [fieldData, setFieldData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  // Warn on navigation with unsaved changes (in-app + browser close/refresh)
  const unsavedBlocker = useUnsavedChanges(dirty);


  const [showHistory, setShowHistory] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [showNoteModal, setShowNoteModal] = useState(null);
  const [transitionNote, setTransitionNote] = useState('');
  const [isBackwardTransition, setIsBackwardTransition] = useState(false);
  const [screenCompletions, setScreenCompletions] = useState([]);
  const [togglingDone, setTogglingDone] = useState(false);
  const [transitionBlockMsg, setTransitionBlockMsg] = useState(null);
  const [showTransitionForm, setShowTransitionForm] = useState(null);
  const [relatedRecords, setRelatedRecords] = useState([]);
  const [relTypes, setRelTypes] = useState([]);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [linkRelType, setLinkRelType] = useState(null);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkResults, setLinkResults] = useState([]);
  const [linkSearching, setLinkSearching] = useState(false);
  const [linkHasMore, setLinkHasMore] = useState(false);
  const [linkOffset, setLinkOffset] = useState(0);
  const [linkNote, setLinkNote] = useState('');
  const { tenant, user } = useAuthStore();
  const [leftOpen, setLeftOpen] = useState(true);
  const [viewingStageId, setViewingStageId] = useState(null);
  const [viewingMode, setViewingMode] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey(k => k + 1); // 'view' | 'edit' | null
  const [chatOpen, setChatOpen] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [aiIndexed, setAiIndexed] = useState(false);
  const { channelUnreadCounts } = useChatContext();

  // Concurrent editing
  const {
    presenceUsers,
    fieldLocks,
    incomingChanges,
    conflict,
    lockField,
    unlockField,
    broadcastFieldUpdate,
    broadcastRecordSaved,
    clearIncomingChange,
    setConflictFromResponse,
    resolveConflict,
    isLockedByOther,
    isConnected,
  } = useConcurrentEditing(recordId);

  const fetchCompletions = async (recId) => {
    try {
      const res = await api.get(`/screen-completions/${recId || recordId}`);
      setScreenCompletions(res.data.completions || []);
    } catch (err) {
      console.error('Failed to fetch completions', err);
    }
  };

  const fetchRelatedRecords = async () => {
    if (!record?.record_type_id) return;
    try {
      const [relRes, typesRes] = await Promise.all([
        api.get('/relationships/records/' + recordId),
        api.get('/relationships/types?record_type_id=' + record.record_type_id),
      ]);
      setRelatedRecords(relRes.data.relationships || []);
      setRelTypes((typesRes.data.relationship_types || []).filter(rt => rt.is_active));
    } catch (err) { console.error('Failed to load related records', err); }
  };

  useEffect(() => { if (record?.record_type_id) fetchRelatedRecords(); }, [record?.record_type_id, recordId]);

  // Check AI index status for the green dot indicator
  useEffect(() => {
    if (!recordId) return;
    aiChatApi.getRecordStatus(recordId)
      .then(data => setAiIndexed(data.totalChunks > 0))
      .catch(() => {});
  }, [recordId]);

  const handleLinkRecord = async (targetId) => {
    if (!linkRelType) return;
    try {
      await api.post('/relationships/records', {
        relationship_type_id: linkRelType.id,
        source_record_id: recordId,
        target_record_id: targetId,
        notes: linkNote || undefined,
      });
      toast.success('Records linked');
      setShowLinkModal(false);
      setLinkSearch('');
      setLinkResults([]);
      setLinkNote('');
      fetchRelatedRecords();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to link'); }
  };

  const handleUnlinkRecord = async (relId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Remove this link?')) return;
    try {
      await api.delete('/relationships/records/' + relId);
      toast.success('Link removed');
      fetchRelatedRecords();
    } catch (err) { toast.error('Failed to unlink'); }
  };

  // Link search effect (GAP 10: with pagination)
  useEffect(() => {
    if (!linkSearch || linkSearch.length < 2 || !linkRelType) { setLinkResults([]); setLinkHasMore(false); return; }
    setLinkOffset(0);
    const timer = setTimeout(async () => {
      setLinkSearching(true);
      try {
        const limit = 20;
        const res = await api.get('/relationships/records/' + recordId + '/search?q=' + encodeURIComponent(linkSearch) + '&relationship_type_id=' + linkRelType.id + '&limit=' + limit + '&offset=0');
        const records = res.data.records || [];
        setLinkResults(records);
        setLinkHasMore(records.length >= limit);
      } catch (err) { console.error(err); }
      finally { setLinkSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [linkSearch, linkRelType, recordId]);

  const loadMoreLinkResults = async () => {
    if (!linkSearch || !linkRelType) return;
    const newOffset = linkOffset + 20;
    setLinkOffset(newOffset);
    try {
      const res = await api.get('/relationships/records/' + recordId + '/search?q=' + encodeURIComponent(linkSearch) + '&relationship_type_id=' + linkRelType.id + '&limit=20&offset=' + newOffset);
      const more = res.data.records || [];
      setLinkResults(prev => [...prev, ...more]);
      setLinkHasMore(more.length >= 20);
    } catch (err) { console.error(err); }
  };

  const openLinkModal = () => {
    setLinkRelType(relTypes.length === 1 ? relTypes[0] : null);
    setLinkSearch('');
    setLinkResults([]);
    setLinkNote('');
    setShowLinkModal(true);
  };

  const isTabCompleted = (screenId, stageId) => {
    return screenCompletions.some(c => c.screen_id === screenId && c.stage_id === stageId);
  };

  const handleToggleDone = async () => {
    if (!record || !tabs || tabs.length === 0) return;
    const currentTab = tabs[activeTab];
    if (!currentTab) return;
    const screenId = currentTab.screen.id;
    const stageId = record.workflow_stage_id;
    setTogglingDone(true);
    try {
      const res = await api.post('/screen-completions/toggle', {
        record_id: recordId,
        screen_id: screenId,
        stage_id: stageId,
      });
      if (res.data.completed) {
        toast.success('Tab marked complete');
      } else {
        toast.success('Tab marked incomplete');
      }
      fetchCompletions();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.missing_fields) {
        const fields = err.response.data.missing_fields;
        toast.error('Missing required fields: ' + fields.join(', '));
      } else {
        toast.error(err.response?.data?.error || 'Failed to update');
      }
    } finally {
      setTogglingDone(false);
    }
  };


  const handleJumpToTab = (screenId) => {
    if (!screenId || tabs.length === 0) return;
    const idx = tabs.findIndex(t => t.screen && t.screen.id === screenId);
    if (idx >= 0) {
      setActiveTab(idx);
      setScreenLayout(tabs[idx].screen.layout);
      // Scroll to top of form area
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  const [rightOpen, setRightOpen] = useState(true);
  const [diaryTypes, setDiaryTypes] = useState([]);
  useEffect(() => {
    client.get('/diary/types').then(({ data }) => {
      setDiaryTypes((data.diary_types || []).filter(dt => !dt.is_system && (dt.tracked_fields || []).length > 0));
    }).catch(() => {});
  }, []);

  const savedConfig = tenant?.settings?.widget_config || DEFAULT_CONFIG;
  // Respect saved widget config — only show diary widgets where admin placed them
  const widgetConfig = (() => {
    const config = JSON.parse(JSON.stringify(savedConfig));
    // Find where old generic 'diary' was placed (if at all)
    let oldDiaryCol = null;
    for (const col of ['left', 'right', 'hidden']) {
      if ((config[col] || []).includes('diary')) {
        oldDiaryCol = col;
        config[col] = config[col].filter(id => id !== 'diary');
      }
    }
    // For new diary types not yet in config, place them where old 'diary' was (default: hidden)
    const allPlaced = [...(config.left || []), ...(config.right || []), ...(config.hidden || [])];
    for (const dt of diaryTypes) {
      const widgetId = 'diary:' + dt.id;
      if (!allPlaced.includes(widgetId)) {
        const targetCol = oldDiaryCol || 'hidden';
        config[targetCol] = [...(config[targetCol] || []), widgetId];
      }
    }
    return config;
  })();

  const renderWidget = (widgetId) => {
    switch (widgetId) {
      case 'vitals': return <VitalsWidget key="vitals" recordId={recordId} record={record} fields={fields} fieldData={fieldData} />;
      case 'summary': return <RecordSummary key="summary" record={record} fields={fields} fieldData={fieldData} />;
      case 'notes': return <NotesWidget key={'notes-'+refreshKey} recordId={recordId} />;
      case 'activity': return <ActivityTimeline key={'activity-'+refreshKey+'-'+activityRefreshKey} recordId={recordId} />;
      case 'diary': return <DiaryWidget key={'diary-'+refreshKey} recordId={recordId} />;
      default:
        if (widgetId?.startsWith('diary:')) {
          const dtId = widgetId.split(':')[1];
          return <DiaryWidget key={'diary-'+dtId+'-'+refreshKey} recordId={recordId} diaryTypeId={dtId} />;
        }
        return null;
      case 'tasks': return <TaskPreviewWidget key={'tasks-'+refreshKey} recordId={recordId} recordTypeId={record?.record_type_id} record={record} onJumpToTab={handleJumpToTab} tabs={tabs} />;
      case 'documents': return <DocumentPreviewWidget key={'documents-'+refreshKey} recordId={recordId} />;
      case 'calendar': return <MiniCalendar key={'calendar-'+refreshKey} recordId={recordId} />;
      case 'team': return <TeamWidget key={'team-'+refreshKey} record={record} />;
      case 'portal_messages': return <PortalMessagesWidget key={'portal-'+refreshKey} recordId={recordId} />;
      case 'communications': return <CommunicationTimeline key={'comms-'+refreshKey} recordId={recordId} />;
    }
  };
  const [transitionFormData, setTransitionFormData] = useState({});
  const [workflowTransitions, setWorkflowTransitions] = useState([]);
  const [showSwitchWf, setShowSwitchWf] = useState(false);
  const [switchWfData, setSwitchWfData] = useState({ workflows: [], selectedWf: null, selectedStage: null, loading: false });
  const [showStagePicker, setShowStagePicker] = useState(null);
  const [stagePickerStages, setStagePickerStages] = useState([]);
  const [selectedTargetStage, setSelectedTargetStage] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try the render endpoint first (returns screen layout if one exists)
        let renderData = null;
        try {
          const renderRes = await adminApi.renderRecord(recordId);
          renderData = renderRes.data;
        } catch (e) {
          // Render endpoint failed, fall back to basic record fetch
        }

        if (renderData) {
          setRecord(renderData.record);
          setFields(renderData.fields);
          setFieldData(renderData.record.field_data || {});
          if (renderData.tabs && renderData.tabs.length > 0) {
            setTabs(renderData.tabs);
            setActiveTab(0);
            setScreenLayout(renderData.tabs[0].screen.layout);
            fetchCompletions(renderData.record?.id || recordId);
          } else if (renderData.screen && renderData.screen.layout) {
            setTabs([]);
            setScreenLayout(renderData.screen.layout);
          }
          if (renderData.transitions) {
            setTransitions(renderData.transitions);
          }
          if (renderData.workflowTransitions) {
            setWorkflowTransitions(renderData.workflowTransitions);
          }
        } else {
          const res = await recordsApi.get(recordId);
          setRecord(res.data.record);
          setFields(res.data.fields);
          setFieldData(res.data.record.field_data || {});
        }
      } catch (err) {
        toast.error('Record not found');
        navigate(`/records/${typeSlug}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [recordId, refreshKey]);

  const handleFieldChange = (fieldId, value) => {
    setFieldData(prev => ({ ...prev, [fieldId]: value }));
    setDirty(true);
    if (errors[fieldId]) setErrors(prev => { const n = { ...prev }; delete n[fieldId]; return n; });
  };

  // Get field IDs visible on the current screen layout (respects visibility rules)
  const getVisibleFieldIds = (data) => {
    if (screenLayout && screenLayout.sections) {
      const ids = new Set();
      for (const section of screenLayout.sections) {
        // Skip entire section if hidden by visibility rules
        if (section.visibility_rules && !evaluateVisibility(section.visibility_rules, data || fieldData)) continue;
        for (const widget of (section.widgets || [])) {
          if (widget.type === 'field' && widget.config?.field_id) {
            // Skip widget if hidden by visibility rules
            if (widget.visibility_rules && !evaluateVisibility(widget.visibility_rules, data || fieldData)) continue;
            ids.add(widget.config.field_id);
          }
        }
      }
      return ids;
    }
    // No screen layout — all fields are visible
    return new Set(fields.map(f => f.id));
  };

  const validateRequired = (data) => {
    const newErrors = {};
    const visibleIds = getVisibleFieldIds(data);

    for (const field of fields) {
      if (!field.is_required) continue;
      if (!visibleIds.has(field.id)) continue;

      const val = data[field.id];

      // CHECKBOX/TOGGLE: false is a valid value
      if (field.field_type === 'CHECKBOX' || field.field_type === 'TOGGLE') continue;

      // NUMBER/CURRENCY: 0 is valid, only null/undefined/empty fails
      if (field.field_type === 'NUMBER' || field.field_type === 'CURRENCY') {
        if (val === null || val === undefined || val === '') {
          newErrors[field.id] = `${field.label} is required`;
        }
        continue;
      }

      // REPEATER/REPEATER_SECTION: empty array or null
      if (field.field_type === 'REPEATER' || field.field_type === 'REPEATER_SECTION') {
        if (!Array.isArray(val) || val.length === 0) {
          newErrors[field.id] = `${field.label} is required`;
        }
        continue;
      }

      // MULTI_SELECT: empty array or null
      if (field.field_type === 'MULTI_SELECT') {
        if (!Array.isArray(val) || val.length === 0) {
          newErrors[field.id] = `${field.label} is required`;
        }
        continue;
      }

      // ADDRESS: null or empty object
      if (field.field_type === 'ADDRESS') {
        if (!val || typeof val !== 'object' || Object.values(val).every(v => !v || !String(v).trim())) {
          newErrors[field.id] = `${field.label} is required`;
        }
        continue;
      }

      // Default: TEXT, TEXTAREA, EMAIL, PHONE, URL, DATE, DATETIME, DROPDOWN, RECORD_LINK, etc.
      if (val === null || val === undefined || (typeof val === 'string' && !val.trim())) {
        newErrors[field.id] = `${field.label} is required`;
      }
    }

    return newErrors;
  };

  const handleSave = async (overrideData) => {
    const dataToSave = overrideData || fieldData;

    // Client-side required field validation
    const requiredErrors = validateRequired(dataToSave);
    if (Object.keys(requiredErrors).length > 0) {
      setErrors(requiredErrors);
      toast.error('Please fix the errors below');
      // Scroll to first error field
      setTimeout(() => {
        const el = document.querySelector('.text-red-600, .text-red-500');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSaving(true);
    setErrors({});
    try {
      const res = await recordsApi.update(recordId, {
        field_data: dataToSave,
        expected_updated_at: record?.updated_at || undefined,
      });
      setRecord(res.data.record);
      setFieldData(res.data.record.field_data || dataToSave);
      setDirty(false);
      toast.success('Record saved');
      broadcastRecordSaved(dataToSave);
      triggerRefresh();
    } catch (err) {
      if (err.response?.status === 409) {
        setConflictFromResponse(err.response.data);
      } else if (err.response?.data?.field_errors) {
        setErrors(err.response.data.field_errors);
        toast.error('Please fix the errors below');
      } else {
        console.error('SAVE ERROR:', err.response?.status, err.response?.data, err.message); toast.error(err.response?.data?.error || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    try {
      await recordsApi.delete(recordId);
      toast.success('Record deleted');
      setDirty(false);
      navigate(`/records/${typeSlug}`);
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const loadHistory = async () => {
    try {
      const res = await recordsApi.getHistory(recordId);
      setHistory(res.data.history);
      setShowHistory(true);
    } catch (err) {
      toast.error('Failed to load history');
    }
  };


  const handleTransition = async (transition) => {
    // Check tab completions before allowing transition
    if (record?.workflow_stage_id) {
      try {
        const checkRes = await api.post('/screen-completions/check-transition', {
          record_id: recordId,
          stage_id: record.workflow_stage_id,
        });
        if (!checkRes.data.can_transition) {
          setTransitionBlockMsg(checkRes.data);
          return;
        }
      } catch (err) {
        console.error('Transition check failed', err);
      }
    }
    // Cross-workflow transition: show stage picker
    if (transition.to_workflow_id && transition.to_workflow_name) {
      try {
        const wfRes = await adminApi.getWorkflows(record.record_type_id);
        const targetWf = wfRes.data.workflows.find(function(w) { return w.id === transition.to_workflow_id; });
        if (targetWf && targetWf.stages && targetWf.stages.length > 0) {
          setStagePickerStages(targetWf.stages);
          setSelectedTargetStage(transition.to_stage_id || targetWf.stages[0].id);
          setShowStagePicker(transition);
          return;
        }
      } catch (e) { console.error(e); }
    }
    if (transition.confirm_message) {
      if (!window.confirm(transition.confirm_message)) return;
    }
    if (transition.screen_layout) {
      setShowTransitionForm(transition);
      setTransitionFormData({});
      return;
    }
    // Detect backward transition (to_stage_sort_order < from_stage_sort_order)
    const isBackward = transition.to_stage_sort_order !== undefined &&
      transition.from_stage_sort_order !== undefined &&
      transition.to_stage_sort_order < transition.from_stage_sort_order;

    if (isBackward || transition.require_note) {
      setIsBackwardTransition(isBackward);
      setShowNoteModal(transition);
      return;
    }
    await executeTransition(transition.id, null);
  };

  const executeTransition = async (transitionId, note, targetStageId) => {
    setTransitioning(true);
    try {
      const res = await recordsApi.transition(recordId, { transition_id: transitionId, note, field_data: transitionFormData, target_stage_id: targetStageId || null });
      setRecord(res.data.record);
      setTransitions(res.data.transitions || []);
      setShowNoteModal(null);
      setTransitionNote('');
      setShowTransitionForm(null);
      setTransitionFormData({});
      toast.success('Record moved to ' + res.data.record.stage_name);
      setViewingStageId(null);
      setViewingMode(null);
      // Re-fetch screen for new stage
      const renderRes = await adminApi.renderRecord(recordId);
      const renderData = renderRes.data;
      if (renderData.tabs && renderData.tabs.length > 0) {
        setTabs(renderData.tabs);
        setActiveTab(0);
        setScreenLayout(renderData.tabs[0].screen.layout);
      } else if (renderData.screen && renderData.screen.layout) {
        setTabs([]);
        setScreenLayout(renderData.screen.layout);
      }
      if (renderData.transitions) {
        setTransitions(renderData.transitions);
      }
      if (renderData.workflowTransitions) {
        setWorkflowTransitions(renderData.workflowTransitions);
      }
      triggerRefresh();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Transition failed');
    } finally {
      setTransitioning(false);
    }
  };

  const getFieldLabel = (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    return field ? field.label : fieldId;
  };

  const formatValue = (val, fieldId) => {
    if (val === null || val === undefined || val === '') return '(empty)';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    // Repeater data: array of row objects
    if (Array.isArray(val)) {
      if (val.length === 0) return '(empty)';
      const field = fields.find(f => f.id === fieldId);
      const columns = field?.config?.columns || [];
      const colLabels = columns.map(c => c.label || c.name || c.key);
      return (
        <div className="mt-1">
          <table className="w-full text-xs border border-gray-200 rounded">
            {colLabels.length > 0 && (
              <thead>
                <tr className="bg-gray-100">
                  {colLabels.map((label, i) => (
                    <th key={i} className="px-2 py-1 text-left font-medium text-gray-600 border-b border-gray-200">{label}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {val.map((row, ri) => {
                const keys = columns.length > 0 ? columns.map(c => c.key || c.name) : Object.keys(row);
                return (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {keys.map((key, ci) => (
                      <td key={ci} className="px-2 py-1 border-b border-gray-100 text-gray-700">{row[key] ?? ''}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <span className="text-gray-400">{val.length} row{val.length !== 1 ? 's' : ''}</span>
        </div>
      );
    }
    // Address or other objects
    if (typeof val === 'object') {
      const parts = Object.entries(val).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`);
      return parts.length > 0 ? parts.join(', ') : '(empty)';
    }
    return String(val);
  };


  const handleViewStage = async (stageId, mode) => {
    setViewingStageId(stageId);
    setViewingMode(mode);
    try {
      const renderRes = await adminApi.renderRecord(recordId, stageId || undefined);
      if (renderRes.data.screen) {
        setScreenLayout(renderRes.data.screen.layout);
        if (renderRes.data.tabs) setTabs(renderRes.data.tabs);
      }
    } catch (err) {
      console.error('Failed to load stage screen:', err);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  // Switch Workflow (admin only)
  const handleOpenSwitchWorkflow = async () => {
    try {
      const res = await adminApi.getWorkflows(record.record_type_id);
      const wfs = (res.data.workflows || []).filter(w => w.id !== record.current_workflow_id);
      if (wfs.length === 0) { toast.error('No other workflows on this record type'); return; }
      setSwitchWfData({ workflows: wfs, selectedWf: null, selectedStage: null, loading: false });
      setShowSwitchWf(true);
    } catch (err) { toast.error('Failed to load workflows'); }
  };

  const handleSwitchWorkflow = async () => {
    if (!switchWfData.selectedWf || !switchWfData.selectedStage) { toast.error('Select a workflow and stage'); return; }
    setSwitchWfData(prev => ({ ...prev, loading: true }));
    try {
      await recordsApi.switchWorkflow(recordId, { workflow_id: switchWfData.selectedWf, stage_id: switchWfData.selectedStage });
      toast.success('Workflow switched');
      setShowSwitchWf(false);
      // Reload
      const renderRes = await adminApi.renderRecord(recordId);
      const renderData = renderRes.data;
      setRecord(renderData.record);
      setScreen(renderData.screen);
      setTabs(renderData.tabs || null);
      setFields(renderData.fields);
      setTransitions(renderData.transitions || []);
      if (renderData.workflowTransitions) setWorkflowTransitions(renderData.workflowTransitions);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Switch failed');
    } finally {
      setSwitchWfData(prev => ({ ...prev, loading: false }));
    }
  };

  if (!record) return null;

  return (
    <div className="flex h-full -m-6">
    <div className={`flex-1 overflow-y-auto p-6 transition-all ${chatOpen || aiChatOpen ? 'max-w-[calc(100%-380px)]' : ''}`}>
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/records/${typeSlug}`)} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{record.title}</h1>
              <span className="text-sm text-gray-400 font-mono">{record.reference_number}</span>
              {record.inbound_email && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(record.inbound_email);
                    toast.success('Case email copied');
                  }}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 transition-colors ml-1"
                  title={`Case email: ${record.inbound_email}`}
                >
                  <Mail className="h-3.5 w-3.5" />
                  <span className="font-mono">{record.inbound_email}</span>
                  <Copy className="h-3 w-3" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-gray-500">{record.record_type_name}</span>
              {record.stage_name && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: record.stage_color + '20', color: record.stage_color }}>
                    {record.stage_name}
                  </span>
                </>
              )}
              {user?.is_admin && record.current_workflow_id && (
                <button onClick={handleOpenSwitchWorkflow}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1 ml-1"
                  title="Switch Workflow (Admin)">
                  <GitBranch className="h-3 w-3" /> Switch
                </button>
              )}
              <span className="text-gray-300">·</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                record.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>{record.status}</span>
              {record.record_type_slug === 'intake' && record.stage_name === 'Retained' && record.status === 'active' && (
                <button onClick={() => setShowConvertModal(true)}
                  className="ml-2 text-xs px-3 py-1 bg-green-600 text-white rounded-full font-medium hover:bg-green-700">
                  Convert to Case →
                </button>
              )}
              {record.status === 'converted' && (
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">Converted</span>
              )}
            </div>
          </div>
        </div>
        <PresenceBar users={presenceUsers} isConnected={isConnected} />
        <div className="flex items-center gap-2">
          <div className="flex-1"><TagsWidget recordId={recordId} category={record?.record_type_slug || 'general'} /></div>
          <button onClick={() => { setAiChatOpen(!aiChatOpen); if (!aiChatOpen) setChatOpen(false); }}
            className={`relative flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              aiChatOpen ? 'text-purple-600 bg-purple-50 border-purple-200' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            <Sparkles className="h-4 w-4" /> Ask AI
            {aiIndexed && !aiChatOpen && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white" />
            )}
          </button>
          <button onClick={() => { setChatOpen(!chatOpen); if (!chatOpen) setAiChatOpen(false); }}
            className={`relative flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${
              chatOpen ? 'text-blue-600 bg-blue-50 border-blue-200' : 'text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}>
            <MessageSquare className="h-4 w-4" /> Chat
            {Object.values(channelUnreadCounts).reduce((s, c) => s + c, 0) > 0 && !chatOpen && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                {Object.values(channelUnreadCounts).reduce((s, c) => s + c, 0)}
              </span>
            )}
          </button>
          <button onClick={loadHistory}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Clock className="h-4 w-4" /> History
          </button>
          <button onClick={() => setShowDocGenModal(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
            Generate Doc
          </button>
          <button onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      </div>


      {/* Related Records Chips */}
      {relTypes.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="flex items-center gap-1 text-xs text-gray-400 font-medium">
            <Link2 className="h-3.5 w-3.5" /> Related:
          </span>
          {relatedRecords.some(r => r.anchor_required && r.is_anchor) && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full font-medium" title="This record is the anchor (lead)">⚓ Anchor</span>
          )}
          {relatedRecords.length === 0 && (
            <span className="text-xs text-gray-300 italic">None linked</span>
          )}
          {relatedRecords.map(rel => (
            <span key={rel.id + '-' + (rel.indirect ? 'ind' : 'dir')}
              className={'inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-xs group transition-colors border ' +
                (rel.indirect ? 'bg-gray-50/50 border-dashed border-gray-200 opacity-75' : 'bg-gray-50 border-gray-200 hover:border-brand-300 hover:bg-brand-50')}>
              {/* GAP 6: Type color dot + role label */}
              <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: rel.color || '#6366F1' }} />
              <span className="text-[10px] text-gray-400 font-medium">{rel.role_label}</span>
              <span className="text-gray-300">·</span>
              <a href={'/records/' + (rel.linked_record.record_type_slug || 'unknown') + '/' + rel.linked_record.id}
                onClick={(e) => { e.preventDefault(); navigate('/records/' + (rel.linked_record.record_type_slug || 'unknown') + '/' + rel.linked_record.id); }}
                className="text-gray-700 hover:text-brand-600 font-medium cursor-pointer">
                {rel.linked_record.title || rel.linked_record.reference_number || 'Untitled'}
              </a>
              {rel.linked_record.stage_name && (
                <span className="text-gray-400 font-normal">&middot; {rel.linked_record.stage_name}</span>
              )}
              {rel.indirect && (
                <span className="text-[10px] text-gray-300 italic" title="Linked indirectly through peer group">indirect</span>
              )}
              {rel.anchor_required && !rel.is_anchor && !rel.indirect && (
                <button onClick={async (e) => { e.preventDefault(); e.stopPropagation(); try { await api.put('/relationships/records/' + rel.id + '/set-anchor', { anchor_record_id: recordId }); toast.success('You are now the anchor'); fetchRelatedRecords(); } catch(err) { toast.error('Failed'); } }}
                  className="ml-0.5 px-1.5 py-0.5 text-[10px] text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Make this record the anchor (lead)">
                  ⚓ Take anchor
                </button>
              )}
              {rel.anchor_required && rel.is_anchor && (
                <span className="ml-0.5 text-[10px] text-amber-400" title="This linked record is currently the anchor">⚓</span>
              )}
              {/* GAP 9: Hide unlink button on indirect peer links */}
              {!rel.indirect && (
                <button onClick={(e) => handleUnlinkRecord(rel.id, e)}
                  className="ml-0.5 p-0.5 text-gray-300 hover:text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove link">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          <button onClick={openLinkModal}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50 border border-dashed border-brand-300 rounded-full transition-colors">
            <Plus className="h-3 w-3" /> Link
          </button>
        </div>
      )}

      {/* Link Record Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="h-4 w-4 text-gray-400" /> Link a Record
              </h3>
              <button onClick={() => setShowLinkModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {relTypes.length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Relationship Type</label>
                  <div className="space-y-1.5">
                    {relTypes.map(rt => (
                      <button key={rt.id} onClick={() => { setLinkRelType(rt); setLinkSearch(''); setLinkResults([]); }}
                        className={'w-full text-left p-2.5 rounded-lg border transition-colors flex items-center gap-2 ' +
                          (linkRelType?.id === rt.id ? 'border-brand-400 bg-brand-50' : 'border-gray-200 hover:border-gray-300')}>
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: rt.color || '#6366F1' }} />
                        <div>
                          <span className="text-xs font-medium text-gray-800">{rt.name}</span>
                          {rt.description && <p className="text-[10px] text-gray-400">{rt.description}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {linkRelType && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Search Records</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                      <input type="text" value={linkSearch} onChange={e => setLinkSearch(e.target.value)}
                        placeholder="Type to search by title or reference..."
                        
                        className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  {linkSearching && <p className="text-xs text-gray-400 text-center py-2">Searching...</p>}
                  {!linkSearching && linkResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-50">
                      {linkResults.map(r => (
                        <button key={r.id} onClick={() => handleLinkRecord(r.id)}
                          className="w-full text-left px-3 py-2.5 hover:bg-brand-50 transition-colors">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-800">{r.title || 'Untitled'}</span>
                            <Plus className="h-3.5 w-3.5 text-brand-600" />
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-gray-400">{r.record_type_name}</span>
                            {r.reference_number && <span className="text-[10px] text-gray-300">{r.reference_number}</span>}
                            {r.stage_name && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full text-white"
                                style={{ backgroundColor: r.stage_color || '#6B7280' }}>
                                {r.stage_name}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* GAP 10: Load more button */}
                  {!linkSearching && linkHasMore && linkResults.length > 0 && (
                    <button onClick={loadMoreLinkResults}
                      className="w-full text-center py-2 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors">
                      Load more results...
                    </button>
                  )}
                  {!linkSearching && linkSearch.length >= 2 && linkResults.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">No matching records found</p>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Note (optional)</label>
                    <input type="text" value={linkNote} onChange={e => setLinkNote(e.target.value)}
                      placeholder="e.g., Same accident, passenger #2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parties Panel */}
      {record && ['intake', 'pi_litigation_mva', 'contact', 'company'].includes(record.record_type_slug) && (
        <div className="mb-4">
          <PartiesPanel recordId={recordId} recordTypeSlug={record.record_type_slug} />
        </div>
      )}

      {/* Stage Progress Path */}
      {(() => {
        const stageSettings = record?.stage_settings || {};
        const isFinal = stageSettings.is_final === true;
        return (
          <>
            <StageProgressPath
              record={record}
              transitions={isFinal ? [] : transitions}
              workflowTransitions={isFinal ? [] : workflowTransitions}
              onTransition={handleTransition}
              transitioning={transitioning}
              onViewStage={handleViewStage}
              viewingStageId={viewingStageId}
              viewingMode={viewingMode}
            />
            {isFinal && (
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 mb-4">
                <span>Final Stage — No further transitions</span>
              </div>
            )}
          </>
        );
      })()}

      {/* Tab Bar */}
      {tabs.length > 1 && (
        <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
          {tabs.map((tab, idx) => (
            <button key={tab.stage_screen_id} onClick={() => { setActiveTab(idx); setScreenLayout(tab.screen.layout); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === idx
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}>
              {isTabCompleted(tab.screen.id, record?.workflow_stage_id) && (
                <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {tab.tab_label || tab.screen.name}
            </button>
          ))}
        </div>
      )}
      {/* 3-column layout */}
      <div className="flex gap-4">
        {/* Left panel */}
        <div className={`flex-shrink-0 transition-all duration-300 ${leftOpen ? 'w-[280px]' : 'w-10'}`}>
          {leftOpen ? (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={() => setLeftOpen(false)} title="Collapse left panel"
                  className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors">
                  <ChevronLeft className="h-4 w-4" />
                </button>
              </div>
              {(widgetConfig.left || []).map(id => renderWidget(id))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 pt-1">
              <button onClick={() => setLeftOpen(true)} title="Expand left panel"
                className="p-2 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 text-gray-500 transition-all">
                <ChevronRight className="h-4 w-4" />
              </button>

            </div>
          )}
        </div>

        {/* Center: Form */}
        <div className="flex-1 min-w-0">
          {/* Viewing another stage banner */}
          {viewingStageId && (
            <div className={"flex items-center gap-2 px-4 py-2.5 rounded-xl mb-4 text-sm font-medium " +
              (viewingMode === 'view' ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-amber-50 text-amber-700 border border-amber-200")}>
              {viewingMode === 'view' ? '👁 Viewing previous stage (read-only)' : '✏️ Editing previous stage — progress unchanged'}
              <button onClick={() => handleViewStage(null, null)}
                className="ml-auto text-xs underline hover:no-underline">Back to current</button>
            </div>
          )}
          {screenLayout ? (
            <>
            <ScreenRenderer
              layout={screenLayout}
              fields={fields}
              fieldData={fieldData}
              onChange={handleFieldChange}
              errors={errors}
              readOnly={viewingMode === 'view'}
              recordId={recordId}
              fieldLocks={fieldLocks}
              incomingChanges={incomingChanges}
              onFieldFocus={lockField}
              onFieldBlur={unlockField}
              onFieldChange={broadcastFieldUpdate}
              clearIncomingChange={clearIncomingChange}
              isLockedByOther={isLockedByOther}
            />
            {/* Tab Done Button */}
            {tabs.length > 0 && !viewingStageId && tabs[activeTab] && record?.workflow_stage_id && (
              <div className="mt-4 flex justify-end">
                <button onClick={handleToggleDone} disabled={togglingDone}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isTabCompleted(tabs[activeTab].screen.id, record.workflow_stage_id)
                      ? 'bg-green-50 text-green-700 border-2 border-green-300 hover:bg-green-100'
                      : 'bg-brand-600 text-white hover:bg-brand-700'
                  }`}>
                  {togglingDone ? (
                    <span>Processing...</span>
                  ) : isTabCompleted(tabs[activeTab].screen.id, record.workflow_stage_id) ? (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Completed — Click to Undo
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Mark as Done
                    </>
                  )}
                </button>
              </div>
            )}
            </>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-6 space-y-5">
                {fields.map((field) => (
                  <FieldInput
                    key={field.id}
                    field={field}
                    value={fieldData[field.id]}
                    onChange={(val) => handleFieldChange(field.id, val)}
                    error={errors[field.id]}
                    recordId={recordId}
                  />
                ))}
              </div>
            </div>
          )}
          {dirty && (
            <div className="sticky bottom-4 mt-4 flex items-center justify-between px-6 py-3 bg-brand-50 border border-brand-200 rounded-xl shadow-lg">
              <p className="text-sm text-brand-700">You have unsaved changes</p>
              <button onClick={() => handleSave()} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50">
                <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
        {/* Right panel */}
        <div className={`flex-shrink-0 transition-all duration-300 ${rightOpen ? 'w-[280px]' : 'w-10'}`}>
          {rightOpen ? (
            <div className="space-y-4">
              <div className="flex justify-start">
                <button onClick={() => setRightOpen(false)} title="Collapse right panel"
                  className="p-1 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              {(widgetConfig.right || []).map(id => renderWidget(id))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 pt-1">
              <button onClick={() => setRightOpen(true)} title="Expand"
                className="p-2 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 text-gray-500 transition-all">
                <ChevronLeft className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Transition Note Modal */}
      {/* Transition Block Modal */}
      {transitionBlockMsg && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Cannot transition yet</h3>
                  <p className="text-sm text-gray-500">Complete all tabs before moving to the next stage</p>
                </div>
              </div>
              <div className="space-y-2 mb-5">
                {(transitionBlockMsg.screens || []).map((s, i) => (
                  <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    s.completed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {s.completed ? (
                      <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className="font-medium">{s.screen_name}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setTransitionBlockMsg(null)}
                className="w-full px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
                OK, I'll complete them
              </button>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-2">
              {isBackwardTransition ? 'Moving backward' : 'Add a note'}
            </h2>
            {isBackwardTransition && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                You are moving backward in the workflow. A note explaining why is required (at least 10 characters).
              </div>
            )}
            <p className="text-sm text-gray-500 mb-3">
              {isBackwardTransition ? 'Transition' : 'A note is required for'}: <strong>{showNoteModal.label}</strong>
            </p>
            <textarea value={transitionNote} onChange={e => setTransitionNote(e.target.value)}
              rows={3} placeholder={isBackwardTransition ? 'Required: Explain why you are moving backward...' : 'Enter your note...'}
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 mb-1 ${
                isBackwardTransition ? 'border-amber-300' : 'border-gray-300'
              }`} />
            <div className="flex items-center justify-between mb-3">
              <span className={`text-xs ${transitionNote.trim().length < 10 && isBackwardTransition ? 'text-red-500' : 'text-gray-400'}`}>
                {transitionNote.trim().length}/10 characters minimum
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowNoteModal(null); setTransitionNote(''); setIsBackwardTransition(false); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => { executeTransition(showNoteModal.id, transitionNote); setIsBackwardTransition(false); }}
                disabled={(isBackwardTransition ? transitionNote.trim().length < 10 : !transitionNote.trim()) || transitioning}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${
                  isBackwardTransition ? 'bg-amber-600 hover:bg-amber-700' : 'bg-brand-600 hover:bg-brand-700'
                }`}>
                {transitioning ? 'Moving...' : isBackwardTransition ? 'Move Backward' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transition Form Modal */}
      {showTransitionForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">{showTransitionForm.label}</h2>
              <button onClick={function() { setShowTransitionForm(null); setTransitionFormData({}); }}
                className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-5">
              <ScreenRenderer
                layout={showTransitionForm.screen_layout}
                fields={fields}
                fieldData={Object.assign({}, fieldData, transitionFormData)}
                onChange={function(fieldId, value) { setTransitionFormData(function(prev) { var n = Object.assign({}, prev); n[fieldId] = value; return n; }); }}
                errors={{}}
                readOnly={false}
              />
              {showTransitionForm.require_note && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note (required)</label>
                  <textarea value={transitionNote} onChange={function(e) { setTransitionNote(e.target.value); }}
                    rows={2} placeholder="Enter a note..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-200">
              <button onClick={function() { setShowTransitionForm(null); setTransitionFormData({}); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={function() { executeTransition(showTransitionForm.id, showTransitionForm.require_note ? transitionNote : null); }}
                disabled={transitioning || (showTransitionForm.require_note && !transitionNote.trim())}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50">
                {transitioning ? 'Moving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage Picker Modal */}
      {showStagePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {showStagePicker.label}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Moving to <span className="font-medium">{showStagePicker.to_workflow_name}</span>. Choose which stage to enter:
            </p>
            <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
              {stagePickerStages.filter(function(s) { return !s.deleted_at; }).map(function(stage) {
                return (
                  <label key={stage.id}
                    className={"flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all " +
                      (selectedTargetStage === stage.id ? "border-brand-500 bg-brand-50" : "border-gray-200 hover:border-gray-300")}>
                    <input type="radio" name="targetStage" value={stage.id}
                      checked={selectedTargetStage === stage.id}
                      onChange={function() { setSelectedTargetStage(stage.id); }}
                      className="text-brand-600" />
                    <div className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-medium text-gray-800">{stage.name}</span>
                    {stage.settings && stage.settings.is_initial && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium ml-auto">START</span>
                    )}
                  </label>
                );
              })}
            </div>
            {showStagePicker.confirm_message && (
              <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-4">{showStagePicker.confirm_message}</p>
            )}
            <div className="flex items-center justify-end gap-3">
              <button onClick={function() { setShowStagePicker(null); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={function() {
                  var t = showStagePicker;
                  setShowStagePicker(null);
                  if (t.require_note) {
                    setShowNoteModal(Object.assign({}, t, { _targetStageId: selectedTargetStage }));
                  } else {
                    executeTransition(t.id, null, selectedTargetStage);
                  }
                }}
                className="px-4 py-2 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700">
                Move to Stage
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">Record History</h2>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="p-5">
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No history yet</p>
              ) : (
                <div className="space-y-4">
                  {history.map(h => {
                    const details = h.details || {};
                    const changes = details.changes || {};
                    const changeEntries = Object.entries(changes);

                    return (
                      <div key={h.id} className="flex gap-3">
                        <div className="h-2 w-2 rounded-full bg-brand-400 mt-2 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900">
                            <span className="font-medium">{h.first_name} {h.last_name}</span>{' '}
                            <span className="text-gray-500">
                              {h.action === 'stage_transition' && details.description
                                ? details.description
                                : h.action === 'stage_transition' && details.from_stage && details.to_stage
                                  ? `Moved from "${details.from_stage}" to "${details.to_stage}"`
                                  : `${h.action} this record`}
                            </span>
                          </p>
                          <p className="text-xs text-gray-400">{formatTimestamp(h.created_at)}</p>

                          {changeEntries.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {changeEntries.map(([fieldId, change]) => (
                                <div key={fieldId} className="text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 overflow-hidden">
                                  <span className="font-medium text-gray-700">{getFieldLabel(fieldId)}</span>
                                  <div className="flex flex-wrap items-start gap-x-2 gap-y-1 mt-0.5 break-words" style={{ overflowWrap: 'anywhere' }}>
                                    <span className="text-red-500 line-through max-w-full">{formatValue(change.old, fieldId)}</span>
                                    <span className="text-gray-400">→</span>
                                    <span className="text-green-600 font-medium max-w-full">{formatValue(change.new, fieldId)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {h.action === 'created' && details.reference_number && (
                            <p className="text-xs text-gray-400 mt-1">Ref: {details.reference_number}</p>
                          )}

                          {h.action === 'stage_transition' && details.note && (
                            <p className="text-xs text-gray-500 mt-1 italic">"{details.note}"</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conflict Resolution Modal */}
      {conflict && (
        <ConflictResolutionModal
          conflict={{
            conflicts: Object.entries(conflict.conflicts || conflict.labeledConflicts || {}).map(([label, c]) => ({
              fieldName: (conflict.conflict_field_ids || [])[Object.keys(conflict.conflicts || conflict.labeledConflicts || {}).indexOf(label)] || label,
              clientValue: c.your_value,
              serverValue: c.server_value,
            })),
          }}
          fieldDefinitions={fields}
          onResolve={(resolvedData) => {
            const merged = { ...(conflict.server_field_data || fieldData), ...resolvedData };
            setFieldData(merged);
            resolveConflict('merge');
            handleSave(merged);
          }}
          onCancel={() => resolveConflict(null)}
        />
      )}

      {/* Switch Workflow Modal (Admin) */}
      {showSwitchWf && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSwitchWf(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-brand-600" />
                <h2 className="text-base font-bold text-gray-900">Switch Workflow</h2>
              </div>
              <button onClick={() => setShowSwitchWf(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {record.workflow_name && (
                <div className="text-xs text-gray-500">Currently in: <span className="font-medium text-gray-700">{record.workflow_name}</span> → <span className="font-medium" style={{color: record.stage_color}}>{record.stage_name}</span></div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Workflow</label>
                <div className="space-y-2">
                  {switchWfData.workflows.map(wf => (
                    <button key={wf.id} onClick={() => setSwitchWfData(prev => ({ ...prev, selectedWf: wf.id, selectedStage: wf.stages?.[0]?.id || null }))}
                      className={"w-full text-left px-4 py-3 rounded-xl border transition-all " + (switchWfData.selectedWf === wf.id ? "border-brand-300 bg-brand-50" : "border-gray-200 hover:border-gray-300")}>
                      <div className="text-sm font-medium text-gray-900">{wf.name}</div>
                      <div className="text-xs text-gray-400">{(wf.stages || []).length} stages</div>
                    </button>
                  ))}
                </div>
              </div>
              {switchWfData.selectedWf && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Enter at Stage</label>
                  <div className="space-y-1.5">
                    {(switchWfData.workflows.find(w => w.id === switchWfData.selectedWf)?.stages || []).map(stage => (
                      <button key={stage.id} onClick={() => setSwitchWfData(prev => ({ ...prev, selectedStage: stage.id }))}
                        className={"w-full text-left px-3 py-2 rounded-lg border transition-all flex items-center gap-2 " + (switchWfData.selectedStage === stage.id ? "border-brand-300 bg-brand-50" : "border-gray-100 hover:border-gray-200")}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color || '#6b7280' }}></span>
                        <span className="text-sm text-gray-700">{stage.name}</span>
                        {switchWfData.selectedStage === stage.id && <ChevronRight className="h-3 w-3 text-brand-500 ml-auto" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowSwitchWf(false)} className="px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
                <button onClick={handleSwitchWorkflow} disabled={!switchWfData.selectedWf || !switchWfData.selectedStage || switchWfData.loading}
                  className="flex-1 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-semibold hover:bg-brand-700 disabled:opacity-50">
                  {switchWfData.loading ? 'Switching...' : 'Switch Workflow'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDocGenModal && record && (
        <GenerateDocumentModal
          recordId={record.id}
          recordTypeId={record.record_type_id}
          onClose={() => { setShowDocGenModal(false); setActivityRefreshKey(k => k + 1); }}
        />
      )}
      {showConvertModal && record && (
        <ConvertToCaseModal
          intake={record}
          onClose={() => setShowConvertModal(false)}
          onConverted={(caseId) => { setShowConvertModal(false); navigate(`/records/${record.record_type_slug}/${caseId}`); }}
        />
      )}
    </div>
    </div>
    {/* Chat Panel */}
    {chatOpen && (
      <RecordChatPanel
        recordId={recordId}
        onClose={() => setChatOpen(false)}
        referenceNumber={record?.reference_number}
      />
    )}
    {/* AI Chat Panel */}
    {aiChatOpen && (
      <AIChatPanel
        recordId={recordId}
        onClose={() => setAiChatOpen(false)}
        referenceNumber={record?.reference_number}
      />
    )}
    <UnsavedChangesModal blocker={unsavedBlocker} />
    </div>
  );
}
